<template>
  <div class="search-results" data-testid="sbx-sc__autocomplete__search-results">
    <div class="left-side">
      <div
        id="autocomplete-popular-searches"
        class="results-section"
        v-if="popularSearches.length"
        data-testid="sbx-sc__autocomplete__search-results__popular-searches"
      >
        <div class="results-section__title">{{ $t('autocomplete.popular_searches') }}</div>
        <autocomplete-result-item
          v-for="(item, index) in popularSearches"
          :key="`popularSearches-${index}`"
          :text="item.title"
          :url="getPopularSearchUrl(item.url)"
          @autocomplete-result-item-clicked="item => onResultItemClicked(item, 'popular')"
        />
      </div>
      <div class="results-section" v-if="ourSelection.length"
           data-testid="sbx-sc__autocomplete__search-results__our-selection">
        <div class="results-section__title">{{ ourSelectionTitle }}</div>
        <autocomplete-result-item
          v-for="(item, index) in ourSelection"
          :key="`ourSelection-${index}`"
          :text="item.phrase"
          :url="getOurSelectionUrl(item)"
          :searchText="searchText"
          @autocomplete-result-item-clicked="item => onResultItemClicked(item, 'selection')"
        />
      </div>
      <div
        class="results-section"
        v-if="locationSuggestions.length"
        data-testid="sbx-sc__autocomplete__search-results__location-suggestions"
      >
        <div class="results-section__title">{{ $t('autocomplete.locations') }}</div>
        <autocomplete-result-item
          v-for="(item, index) in locationSuggestions"
          :key="`locationSuggestions-${index}`"
          :text="item.label"
          :url="getLocationUrl(item)"
          :searchText="searchText"
          :category="item.category"
          @autocomplete-result-item-clicked="item => onResultItemClicked(item, 'location')"
        />
      </div>
    </div>
    <div
      class="results-section products-section"
      v-if="productSuggestions.length"
      data-testid="sbx-sc__autocomplete__search-results__product-suggestions"
    >
      <div class="results-section__title">{{ $t('autocomplete.products') }}</div>
      <autocomplete-product-item
        v-for="item in productSuggestions"
        :key="item.identifier"
        :item="item"
        :currencyFormat="localeCurrencyFormat"
        @product-item-clicked="onProductItemClicked"
      />
      <button
        v-text="$t('autocomplete.view_all_experiences')"
        @click="onClickAllExperiences"
        type="submit"
        class="products-section__all-experiences"
      ></button>
    </div>
  </div>
</template>

<script>
import AutocompleteProductItem from './AutocompleteProductItem.vue';
import AutocompleteResultItem from './AutocompleteResultItem.vue';
import { withContext } from '@/utils/mixins/withContext';
import { locationURLBuilder, ourSelectionURLBuilder, popularSearchURLBuilder } from './utils';
import { mediumUp } from '@/utils/mediaQueries';
import { handleSearchResultTracking, handleClickedItemResultTracking } from './AutocompleteTrackingHandler';

export default {
  props: {
    locationSuggestions: {
      type: Array,
      default() {
        return [];
      },
    },
    productSuggestions: {
      type: Array,
      default() {
        return [];
      },
    },
    ourSelection: {
      type: Array,
      default() {
        return [];
      },
    },
    popularSearches: {
      type: Array,
      default() {
        return [];
      },
    },
    searchText: { type: String, default: '' },
    submitAction: { type: String, default: '' },
    searchByLocationEnabled: {
      type: Boolean,
      default: false,
    },
  },
  components: { AutocompleteProductItem, AutocompleteResultItem },
  mixins: [withContext],
  data() {
    return {
      ourSelectionTitle: '',
    };
  },
  created() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    getLocationUrl(item) {
      return locationURLBuilder(this.submitAction, item, this.searchByLocationEnabled, t => this.$t(t));
    },
    getPopularSearchUrl(item) {
      return popularSearchURLBuilder(this.submitAction, item);
    },
    getOurSelectionUrl(item) {
      return ourSelectionURLBuilder(this.submitAction, item.url);
    },
    handleResize() {
      const key = mediumUp().matches ? 'autocomplete.our_selection' : 'autocomplete.mobile.our_selection';
      const newOurSelectionTitle = this.$t(key);

      if (this.ourSelectionTitle === newOurSelectionTitle) return;
      this.ourSelectionTitle = newOurSelectionTitle;
    },
    onClickAllExperiences() {
      handleClickedItemResultTracking('search results view all click', 'view all click', this.searchText);
    },
    onProductItemClicked(data) {
      this.submitHandleSearchResultTracking();
      handleClickedItemResultTracking('search results click', 'result click', this.searchText, 'product', data.identifier.toString());
    },
    onResultItemClicked(item, itemType) {
      this.submitHandleSearchResultTracking();
      handleClickedItemResultTracking('search results click', 'result click', this.searchText, itemType, item.text);
    },
    submitHandleSearchResultTracking() {
      handleSearchResultTracking('search results shown', 'show results', {
        searchText: this.searchText,
        ourSelectionItems: this.ourSelection,
        locationSuggestions: this.locationSuggestions,
        productSuggestions: this.productSuggestions,
        popularSearches: this.popularSearches.map(popularSearch => popularSearch.title),
        translate: t => this.$t(t),
      });
    },
  },
  computed: {
    localeCurrencyFormat() {
      const { currency } = this.context;
      return currency;
    },
  },
};
</script>

<style scoped lang="scss">
@import './Autocomplete.brands.scss';

$search-height-primary-mobile: rem-calc(50);
$search-height-primary: rem-calc(40);

.search-results {
  top: $search-height-primary-mobile + rem-calc(6);
  width: calc(100vw + 0.5rem);
  display: flex;
  position: absolute;
  z-index: 10;
  background: white;
  flex-direction: column;
  flex-wrap: nowrap;
  padding: 2rem 1rem 0;
  margin: -1rem -1.25rem 0;
  overflow: auto;
  @media #{$small-only} {
    height: 100vh;
  }


  @media #{$medium-up} {
    top: $search-height-primary + rem-calc(6);
    width: rem-calc(600);
    flex-direction: row;
    gap: rem-calc(30);
    box-shadow: $search-box-shadow;
    border-radius: 5px;
    padding: rem-calc(32);
  }

  @media #{$large-up} {
    width: rem-calc(700);
  }

  .results-section {
    padding-bottom: rem-calc(20);

    &__title {
      padding: 0 0 rem-calc(0.75) 0;
      font-size: rem-calc(17);
      line-height: rem-calc(21);
      font-weight: bold;
    }
  }

  .left-side {
    flex: 4;
  }

  .products-section {
    flex: 6;
  }

  .left-side,
  .products-section {
    &:empty {
      display: none;
    }
  }

  .products-section {
    font-weight: bold;

    &__all-experiences {
      display: inline-block;
      width: rem-calc(260);
      max-width: 100%;
      margin-top: rem-calc(12);
      padding: rem-calc(12 0);
      text-align: center;
      border: 2px solid $cta-color;
      border-radius: 5px;
      text-decoration: none;
      background-color: white;
      color: $cta-color;
      font-size: rem-calc(15);
      line-height: rem-calc(19);
      font-weight: bold;
      cursor: pointer;
    }
  }
}
</style>
